import { useRef, useEffect, useCallback, useState } from "react";
import { createHeader, createReceiptRow } from "@utils/utils";
import ReceiptsUI from "./ReceiptsUI";
import { receiptService } from "@services/ReceiptService";
import { ITableRow, IDataPage } from "../../../types/global";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import { authService } from "@services/authService";
const Receipts = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ITableRow[]>([]);
  const [receipts, setReceipts] = useState<IDataPage>({});
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>(undefined);
  const [expiredFilter, setExpiredFilter] = useState(0);
  const isAdmin = authService.isAdmin();
  const debouncedSearch = useRef(debounce(value => setSearch(value), 800)).current;
  const rowsPerPage = 6;
  const handleStatusFilter = (option: string) => {
    switch (option) {
      case "1":
        setStatusFilter(true);
        break;
      case "2":
        setStatusFilter(false);
        break;
      default:
        setStatusFilter(undefined);
    }
  };
  const handleExpiredFilter = (option: number) => {
    setExpiredFilter(option);
  };
  const getAllReceipts = useCallback(async (skipPage = 0, localSearch = "", localStatusFilter: boolean | undefined = statusFilter, localExpiredFilter: number | undefined = expiredFilter) => {
    try {
      setIsLoading(true);
      const res = await receiptService.getAll(rowsPerPage, skipPage * rowsPerPage, localSearch, localStatusFilter, localExpiredFilter);
      const localPages = Math.ceil(res.data.totalCount / rowsPerPage);
      if (localPages < currentPage) {
        setCurrentPage(1);
        return;
      }
      setPages(localPages);
      setReceipts(rec => ({
        ...rec,
        [skipPage + 1]: {
          expiredFilter: localExpiredFilter,
          search: localSearch,
          statusFilter: localStatusFilter,
          items: [...res.data.items.map(receipt => {
            const summary = receipt.receiptSummay;
            return createReceiptRow(receipt.id, receipt.name, receipt.clientName, receipt.identification, receipt.creationTime, receipt.limitDate, summary.receiptTotal - summary.returnTotal - summary.totalPaid, receipt.status, receipt.receiptType);
          })]
        }
      }));
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, currentPage, expiredFilter]);
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);
  useEffect(() => {
    setRows([]);
    const data = receipts[currentPage];
    if (data && data.search === search && data.statusFilter === statusFilter && data.expiredFilter === expiredFilter) {
      setRows(data.items);
    } else {
      getAllReceipts(currentPage - 1, search, statusFilter, expiredFilter);
    }
  }, [receipts, getAllReceipts, currentPage, search, statusFilter, expiredFilter]);
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);
  const headers = [createHeader("Nombre", "name"), createHeader("Cliente", "clientName"), createHeader("Documento", "identification"), createHeader("Fecha de creación", "creationTime"), createHeader("Fecha límite", "limitDate"), createHeader("Saldo", "balance"), createHeader("Estado", "status"), createHeader("Acciones", "actions")];
  return <ReceiptsUI isLoading={isLoading} showError={showError} closeError={() => setShowError(false)} errorMessage="Hubo problemas para consultar" headers={headers} rows={rows} onClick={() => navigate("/recibos/crear")} pages={pages} currentPage={currentPage} onChangePage={setCurrentPage} onSearch={(value: string) => debouncedSearch(value)} onSearchClick={() => getAllReceipts(currentPage - 1, search)} isAdmin={isAdmin} handleStatusFilter={handleStatusFilter} handleExpiredFilter={handleExpiredFilter} />;
};
export default Receipts;